import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

import { Breadcrumbs, Button } from '@mui/material';
import { BLANK_PDF, Template as PdfmeTemplate } from '@pdfme/common';
import { Viewer } from '@pdfme/ui';
import { Form, Formik } from 'formik';
import * as Yup from 'yup';

import { paths, routes } from 'src/app/routes';
import Page from 'src/layouts/BaseLayout/components/Page/Page';
import Card from 'src/shared/components/Card/Card';
import Input from 'src/shared/components/Input/Input';
import PicturePicker from 'src/shared/components/PicturePicker/PicturePicker';
import Spinner from 'src/shared/components/Spinner/Spinner';
import { toDesignerTemplate } from 'src/shared/components/TemplateDesigner/templateMapping';
import { getPlugins } from 'src/shared/components/TemplateDesigner/utils';
import { FORM_TYPES } from 'src/shared/enums';

import { COMPANIES } from 'src/features/companies/slices/companiesSlice';
import { useLazyGetDocumentTemplateQuery } from 'src/features/documents/api/documentsApi';
import { useCheckAuthorization, useCheckError } from 'src/features/hooks';
import { CONTAINER_ID_ACTION } from 'src/features/notifications/components/NotificationContainer/NotificationContainer';
import { State } from 'src/features/store/store';
import { useUpdateTemplateMutation } from 'src/features/templates/api/templatesApi';
import { CreateTemplateRequest } from 'src/features/templates/models';
import { TEMPLATES } from 'src/features/templates/slices/templatesSlice';

import { SettingsContext } from '../Settings/SettingsContext';

import './TemplateForm.scss';

const INITIAL_VALUES = {
  name: '',
  description: '',
  image: '',
};

const validationSchema = Yup.object({
  name: Yup.string().required('This field is required'),
  description: Yup.string().required('This field is required'),
  image: Yup.string().required('This field is required'),
});

const formTypes = {
  [FORM_TYPES.EDIT]: {
    title: 'Edit template',
    button: 'Edit template',
  },
  [FORM_TYPES.VIEW]: {
    title: 'Template details',
    button: 'Close',
  },
};

type TemplateFormProps = {
  type: Exclude<FORM_TYPES, FORM_TYPES.CREATE>;
};

const EMPTY_PREVIEW: PdfmeTemplate = {
  basePdf: BLANK_PDF,
  schemas: [{}],
  sampledata: [{}],
};

const viewerPlugins = (() => {
  const plugins = getPlugins();
  return { ...plugins, image: plugins.Image };
})();

const TemplateForm = ({ type }: TemplateFormProps): JSX.Element => {
  const navigate = useNavigate();
  const settingsContext = useContext(SettingsContext);

  useCheckAuthorization();

  const template = useSelector((s: State) => s[TEMPLATES].template);
  const currentCompany = useSelector((s: State) => s[COMPANIES].company);

  const initialFormValues = useMemo<CreateTemplateRequest>(
    () => template ?? INITIAL_VALUES,
    [template]
  );
  const [updateTemplate, updatingResult] = useUpdateTemplateMutation();
  const [getDocumentTemplate] = useLazyGetDocumentTemplateQuery();
  const [previewTemplate, setPreviewTemplate] = useState<PdfmeTemplate | null>(
    null
  );
  const [isDesignLoading, setIsDesignLoading] = useState(false);
  const viewerRef = useRef<HTMLDivElement | null>(null);
  const viewer = useRef<Viewer | null>(null);
  const previewTemplateRef = useRef<PdfmeTemplate | null>(null);
  previewTemplateRef.current = previewTemplate;

  const isView = type === FORM_TYPES.VIEW;
  const designTemplateUid = template?.designTemplateUid ?? '';

  const onEditTemplate = (values: CreateTemplateRequest) => {
    template && updateTemplate({ ...values, id: template?.uid });
  };

  useCheckError(updatingResult.isError, 'Error updating the template');

  useEffect(() => {
    if (updatingResult.isSuccess) {
      navigate(paths.settingsTemplates);
      toast.success('Updated successfully', {
        containerId: CONTAINER_ID_ACTION,
      });
    }
  }, [navigate, updatingResult.isSuccess]);

  useEffect(() => {
    if (!isView || !designTemplateUid) {
      return undefined;
    }

    let cancelled = false;
    setPreviewTemplate(null);
    setIsDesignLoading(true);
    getDocumentTemplate(designTemplateUid)
      .unwrap()
      .then((design) => {
        if (cancelled) {
          return;
        }
        setPreviewTemplate(
          toDesignerTemplate(design, template?.renderParams ?? {})
        );
      })
      .catch(() => {
        if (!cancelled) {
          toast.error('Could not load the template design', {
            containerId: CONTAINER_ID_ACTION,
          });
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsDesignLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [designTemplateUid, getDocumentTemplate, isView, template?.renderParams]);

  useEffect(() => {
    if (!isView || !designTemplateUid || !viewerRef.current) {
      return undefined;
    }

    const initialTemplate = previewTemplateRef.current ?? EMPTY_PREVIEW;
    const instance = new Viewer({
      domContainer: viewerRef.current,
      template: initialTemplate,
      inputs: initialTemplate.sampledata ?? [{}],
      plugins: viewerPlugins,
    });
    viewer.current = instance;

    return () => {
      instance.destroy();
      viewer.current = null;
    };
  }, [designTemplateUid, isView]);

  useEffect(() => {
    if (viewer.current && previewTemplate) {
      viewer.current.updateTemplate(previewTemplate);
      viewer.current.setInputs(previewTemplate.sampledata ?? [{}]);
    }
  }, [previewTemplate]);

  const backPath =
    settingsContext?.prevPage === 'Company'
      ? settingsContext?.getPath(
          currentCompany?.uid,
          routes.companies.templates
        )
      : paths.settingsTemplates;

  return (
    <Page>
      <div className="create-template">
        <Breadcrumbs>
          <Link className="create-template__bread-crumbs" to={backPath}>
            {settingsContext?.prevPage ?? 'Templates'}
          </Link>
          <h1>{formTypes[type].title}</h1>
        </Breadcrumbs>
        <div className={isView ? 'template-form__content' : undefined}>
          <Card className="create-template__card">
            <Formik
              initialValues={initialFormValues}
              enableReinitialize
              onSubmit={
                type === FORM_TYPES.EDIT
                  ? onEditTemplate
                  : () => console.log('submit')
              }
              validationSchema={validationSchema}
            >
              <Form className="create-template-form">
                <PicturePicker
                  title="Template picture"
                  name="image"
                  className="create-template-form__picture"
                  disabled={type === FORM_TYPES.VIEW}
                />
                <div className="create-template-form__fields">
                  <Input
                    name="name"
                    placeholder="Name your template"
                    label="Name"
                    disabled={type === FORM_TYPES.VIEW}
                  />
                  <Input
                    name="description"
                    placeholder="What is this template about?"
                    label="Description"
                    disabled={type === FORM_TYPES.VIEW}
                  />
                </div>
                <div className="create-template-form__buttons">
                  <Button
                    color="secondary"
                    className="settings__form-button_secondary"
                    component={Link}
                    to={backPath}
                  >
                    {type === FORM_TYPES.VIEW
                      ? formTypes[type].button
                      : 'Discard'}
                  </Button>
                  {type !== FORM_TYPES.VIEW && (
                    <Button variant="contained" type="submit">
                      {formTypes[type].button}
                    </Button>
                  )}
                </div>
              </Form>
            </Formik>
          </Card>
          {isView && designTemplateUid ? (
            <div className="template-form-preview">
              {isDesignLoading ? (
                <div className="template-form-preview__loading">
                  <Spinner />
                </div>
              ) : null}
              <div className="template-form-preview__canvas" ref={viewerRef} />
            </div>
          ) : null}
          {isView && !designTemplateUid ? (
            <p className="template-form-preview__empty">
              This template has no design.
            </p>
          ) : null}
        </div>
      </div>
    </Page>
  );
};

export default TemplateForm;
